import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';

export interface DataItem {
  marca?: string;
  categoria?: string;
  sitio?: string;
  cantidad: number;
  porcentaje: number;
}

interface DonutChartWithTableProps {
  data: DataItem[];
  totalPublicaciones?: number;
  title: string;
  subtitle: string;
  dataKey: 'marca' | 'categoria' | 'sitio';
  colors: string[];
  tableName?: string;
  isClickable?: boolean;
  onItemClick?: (item: DataItem) => void;
}

const DonutChartWithTable: React.FC<DonutChartWithTableProps> = ({
  data,
  totalPublicaciones,
  title,
  subtitle,
  dataKey,
  colors,
  tableName,
  isClickable = false,
  onItemClick
}) => {
  const { t } = useTranslation();
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('es-ES').format(num);
  };

  const formatPercentage = (num: number | string | undefined): string => {
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (numValue === undefined || numValue === null || isNaN(numValue)) {
      return '0.0%';
    }
    return `${numValue.toFixed(1)}%`;
  };

  // Preparar datos para el gráfico (solo top 10)
  const topData = data.slice(0, 10);
  const chartData = topData.map((item, index) => ({
    name: item[dataKey] || t('statistics.common_labels.no_data'),
    value: item.cantidad,
    porcentaje: item.porcentaje,
    color: colors[index % colors.length]
  }));

  // Agregar "Otros" si hay más elementos
  const restData = data.slice(10);
  if (restData.length > 0) {
    const totalOtros = restData.reduce((sum, item) => sum + item.cantidad, 0);
    const totalItems = data.reduce((sum, item) => sum + item.cantidad, 0);
    const porcentajeOtros = parseFloat(((totalOtros / totalItems) * 100).toFixed(2));
    
    chartData.push({
      name: t('statistics.common_labels.others'),
      value: totalOtros,
      porcentaje: porcentajeOtros,
      color: '#E5E7EB'
    });
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.payload.name}</p>
          <p className="text-sm text-gray-600">
            {t('statistics.common_labels.quantity')}: <span className="font-medium">{formatNumber(data.value)}</span>
          </p>
          <p className="text-sm text-gray-600">
            {t('statistics.common_labels.percentage')}: <span className="font-medium">{formatPercentage(data.payload.porcentaje)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, payload }: any) => {
    if (percent < 0.03) return null; // No mostrar etiquetas para segmentos muy pequeños

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25; // Posicionar más lejos del gráfico
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="black"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="600"
      >
        {`${payload.porcentaje.toFixed(1)}%`}
      </text>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de dona */}
        <div className="flex flex-col items-center justify-center">
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={120}
                  innerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tabla de datos */}
        <div className="flex flex-col">
          <div className="overflow-y-auto max-h-64">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-3/5">
                    {dataKey === 'marca' ? t('statistics.table_headers.brand') : dataKey === 'categoria' ? t('statistics.table_headers.category') : t('statistics.table_headers.site')}
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                    {t('statistics.table_headers.quantity')}
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                    %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topData.map((item, index) => (
                  <tr 
                    key={index} 
                    className={`hover:bg-gray-50 ${isClickable ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (isClickable && onItemClick) {
                        onItemClick(item);
                      }
                    }}
                  >
                    <td className="px-2 py-3">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded mr-2 flex-shrink-0"
                          style={{ backgroundColor: colors[index % colors.length] }}
                        />
                        <div className={`text-sm font-medium text-gray-900 truncate max-w-[180px] ${isClickable ? 'hover:text-blue-600' : ''}`} title={item[dataKey] || t('statistics.common_labels.no_data')}>
                          {item[dataKey] || t('statistics.common_labels.no_data')}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right text-sm text-gray-900 font-medium">
                      {formatNumber(item.cantidad)}
                    </td>
                    <td className="px-2 py-3 text-right text-sm text-gray-600">
                      {formatPercentage(item.porcentaje)}
                    </td>
                  </tr>
                ))}
                {restData.length > 0 && (
                  <tr className="hover:bg-gray-50 border-t-2 border-gray-300">
                    <td className="px-2 py-3">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded mr-2 flex-shrink-0"
                          style={{ backgroundColor: '#E5E7EB' }}
                        />
                        <div className="text-sm font-medium text-gray-500 italic">
                          {t('statistics.common_labels.others')} ({restData.length})
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right text-sm text-gray-600 font-medium">
                      {formatNumber(restData.reduce((sum, item) => sum + item.cantidad, 0))}
                    </td>
                    <td className="px-2 py-3 text-right text-sm text-gray-500">
                      {formatPercentage(parseFloat(((restData.reduce((sum, item) => sum + item.cantidad, 0) / data.reduce((sum, item) => sum + item.cantidad, 0)) * 100).toFixed(2)))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Resumen total */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('statistics.common_labels.total_elements')}:</span>
              <span className="font-medium text-gray-900">
                {data.length}
              </span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-600">{t('statistics.common_labels.total_publications')}:</span>
              <span className="font-medium text-gray-900">
                {formatNumber(totalPublicaciones || data.reduce((sum, item) => sum + item.cantidad, 0))}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonutChartWithTable; 